import type { Operation } from '@apollo/client';
import debug from 'debug';

const log = debug('gql.readFromWriteDBHTTPHeaderManager');

// TODO: Disbale when stable for a bit.
log.enabled = true;

/**
 * All queries will be directed to the write DB (to avoid stale data issues)
 *  for a {{window_ms}} when any {{mutations}} are completed successfully.
 */
export type ReadFromWriteDBConfig = {
  mutations: Array<string>;
  window_ms: number;
};

export const readFromWriteDBHTTPHeaderManager = (() => {
  let _initialised = false;
  let _enabled = false;

  /**
   * We track last mutation trigger in-memory which means
   *    the read-from-write-db behaviour does not survive page reloads.
   *    It's pretty rare and weird to do a full page reoad after a mutation
   *    but if that needs to be supported in the future just dump
   *    the below state into local storage and hydrate during initialisation.
   */
  let lastMutationTrigger: {
    operationName: string;
    mutationName: string;
    timestamp: number;
  } | null = null;

  let readFromWriteDBMutations: Array<string> = [];
  let readFromWriteDBWindow_ms: number = 0;

  /**
   * Only initialise based on config on the first call,
   *    no need to by super dynamic.
   */
  const init = (args: { readFromWriteDBConfig?: ReadFromWriteDBConfig }) => {
    if (_initialised) {
      return;
    }

    _initialised = true;

    if (!args.readFromWriteDBConfig) {
      return;
    }

    _enabled = true;

    readFromWriteDBWindow_ms = args.readFromWriteDBConfig.window_ms;
    onReadFromWriteDBMutationsUpdated({
      mutations: args.readFromWriteDBConfig.mutations,
    });
  };

  const onReadFromWriteDBMutationsUpdated = (args: {
    mutations: Array<string>;
  }) => {
    readFromWriteDBMutations = [...args.mutations];
  };

  const onOperationFinished = (args: { operation: Operation }) => {
    if (!_enabled) {
      return;
    }

    if (
      args.operation.getContext().response?.status === 200 &&
      /**
       * This is the cheapest way to detect if an operation is a mutation.
       * We don't want to do expensive array look ups on every single operation.
       */
      'optimisticResponse' in args.operation.getContext()
    ) {
      /**
       * Mutation is always the first document and there can only be
       *  1 mutation. If this assumption changes below code will have
       *  to be updated.
       */
      const potentialMutation = args.operation.query.definitions[0];

      if (
        potentialMutation.kind === 'OperationDefinition' &&
        potentialMutation.operation === 'mutation' &&
        potentialMutation.name?.value
      ) {
        const whiteListedMutationSelectionNode = potentialMutation.selectionSet.selections.find(
          (selectionSet) => {
            return (
              selectionSet.kind === 'Field' &&
              selectionSet.name.kind === 'Name' &&
              readFromWriteDBMutations.includes(selectionSet.name.value)
            );
          }
        );

        if (whiteListedMutationSelectionNode?.kind === 'Field') {
          lastMutationTrigger = {
            operationName: potentialMutation.name?.value,
            mutationName: whiteListedMutationSelectionNode.name.value,
            timestamp: Date.now(),
          };
        }
      }
    }
  };

  const getHeaders = () => {
    if (!_enabled) {
      return;
    }

    if (lastMutationTrigger === null) {
      return;
    }

    const timeSinceLastMutationTrigger_ms =
      Date.now() - lastMutationTrigger.timestamp;

    if (timeSinceLastMutationTrigger_ms > readFromWriteDBWindow_ms) {
      lastMutationTrigger = null;
      return;
    }

    log(
      `Reading from write db for the next ${
        (readFromWriteDBWindow_ms - timeSinceLastMutationTrigger_ms) / 1000
      } seconds because of operation ${
        lastMutationTrigger.operationName
      } and mutation ${lastMutationTrigger.mutationName}.`
    );

    return {
      'X-Override-DataSource': 'true',
    };
  };

  return {
    init,
    onOperationFinished,
    getHeaders,
    onReadFromWriteDBMutationsUpdated,
  };
})();
